import React, { cloneElement } from "react";
import { Box, IconButton, SxProps, Typography, useMediaQuery, useTheme } from "@mui/material";
import { motion } from "framer-motion";
import { colors } from "../ui/colors";
import { Theme } from "@emotion/react";
import { EditableCompanyAvatar } from "../common/CompanyAvatar";
import { Settings } from "@mui/icons-material";
import { BaseModalComponent } from "./BaseModalComponent";
import SettingsCompany from "./SettingCompany";

export const getVariantStyles = (variant) => {
  switch (variant) {
    case "success":
      return {
        iconBg: colors.success[50],
        iconColor: colors.success[600],
        accentColor: colors.success[300],
        dataColor: colors.success[700],
      };
    case "warning":
      return {
        iconBg: colors.orange[50],
        iconColor: colors.orange[600],
        accentColor: colors.orange[400],
        dataColor: colors.orange[700],
      };
    case "info":
      return {
        iconBg: colors.info[50],
        iconColor: colors.info[600],
        accentColor: colors.info[300],
        dataColor: colors.info[700],
      };
    default:
      return {
        iconBg: colors.orange[50],
        iconColor: colors.orange[600],
        accentColor: colors.orange[300],
        dataColor: colors.orange[700],
      };
  }
};

interface Props {
  swapyName: string;
  sxProp?: SxProps<Theme>;
  sx?: SxProps<Theme>;
  icon: any;
  title: string;
  description: string;
  data?: React.ReactNode;
  children?: any;
  refResizeObserver?: React.RefObject<HTMLDivElement>;
  variant?: "success" | "warning" | "info" | "default";
  config?: boolean;
}

export const WidgetComponentMaps = ({
  swapyName,
  sxProp: sxPropInput,
  sx,
  icon,
  title,
  description,
  data,
  children,
  refResizeObserver,
  config,
  variant = "default",
  ...props
}: Props) => {
  const sxProp = sxPropInput || sx;
  const childrenArray = React.Children.toArray(children);

  const theme = useTheme();

  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  const variantStyles = getVariantStyles(variant);

  return (
    <Box data-swapy-slot={swapyName}>
      <Box
        data-swapy-item={isXs ? null : swapyName}
        {...props}
        ref={refResizeObserver}
        component={motion.div}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        whileHover={{
          y: -2,
          transition: { duration: 0.2 },
        }}
        sx={{
          cursor: "grab",
          bgcolor: "var(--fx-surface-base, #FFFFFF)",
          borderRadius: "16px",
          padding: "24px",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          border: `1px solid ${colors.gray[100]}`,
          boxShadow: "0 8px 12px -2px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
          minHeight: "180px",
          overflow: "auto",
          "&:hover": {
            boxShadow: "0 12px 20px -4px rgba(251, 142, 0, 0.08), 0 4px 8px -2px rgba(0, 0, 0, 0.08)",
            borderColor: colors.orange[200],
            bgcolor: colors.warmWhite.soft,
          },
          "&:active": {
            cursor: "grabbing",
            transform: "scale(0.99)",
          },
          ...sxProp,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            width: "100%",
            mb: 2,
            gap: 2,
          }}
        >
          {cloneElement(icon, {
            sx: {
              fontSize: 24,
              bgcolor: variantStyles.iconBg,
              color: variantStyles.iconColor,
              padding: "12px",
              borderRadius: "12px",
              width: "48px",
              height: "48px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: `1px solid ${colors.orange[100]}`,
              transition: "all 0.2s ease",
            },
          })}

          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              fontSize: "22px",
              color: colors.gray.base,
              lineHeight: 1.3,
              letterSpacing: "-0.01em",
            }}
          >
            {title}
          </Typography>

          {config && (
            <>
              <BaseModalComponent
                modalLabel="Setting"
                elementTrigger={
                  <IconButton>
                    <Settings />
                  </IconButton>
                }
              >
                <SettingsCompany />
              </BaseModalComponent>
            </>
          )}
        </Box>

        {childrenArray[0] && (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mb: 1,
            }}
          >
            {childrenArray[0]}
          </Box>
        )}

        {/* Contenido principal */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          <Box>
            <Typography
              variant="body2"
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "start",
                color: colors.gray[400],
                fontSize: "14px",
                lineHeight: 1,
                mb: 1,
                fontWeight: 400,
              }}
            >
              {description}
            </Typography>
          </Box>

          {/* Número principal con gradiente sutil naranja-amarillo */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "flex-end",
              mt: "auto",
            }}
          ></Box>
        </Box>

        {React.isValidElement(childrenArray[1]) && (
          <Box>
            {cloneElement(childrenArray[1] as React.ReactElement<{ sx?: any }>, {
              sx: {
                bgcolor: "var(--fx-surface-base, #FFFFFF)",
                borderRadius: "8px",
                padding: "6px 10px",
                fontSize: "12px",
                fontWeight: 500,
                color: colors.gray[600],
                ...((childrenArray[1] as React.ReactElement<{ sx?: any }>).props?.sx ?? {}),
              },
            })}
          </Box>
        )}

        {/* Indicador muy discreto */}
        <Box
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            width: 4,
            height: 4,
            borderRadius: "50%",
            bgcolor: colors.orange[400],
            opacity: 0.4,
            transition: "opacity 0.2s ease",
            "&:hover": {
              opacity: 0.7,
            },
          }}
        />
      </Box>
    </Box>
  );
};
